import React, { useEffect, useState } from 'react';
import { getDayResults, getAllResults, DayResults as DayResultsData, AllResults, DayResultRunner, DayResultRace } from '../api/feeds';

// Day Results — every race for the day, grouped by meeting, with Win/Place BSP + jockey/trainer.
// 1st is the Betfair WIN-market winner (reliable). Place-getters are 2nd–4th as a labelled
// approximation (win BSP order) because Betfair does not publish finishing order (see
// SPEC_Day_Results_Page.md). Win and place BSP are read from their own markets — never conflated.
// The "All" button switches to the full cross-date history, paginated 1000 races per page.

const bsp = (v: number | null) => (v != null ? `$${Number(v).toFixed(2)}` : '—');
const cleanName = (n: string) => n.replace(/^\d+\.\s*/, ''); // strip leading saddlecloth number

// rank · horse · barrier · jockey · trainer · win BSP · place BSP
const ROW_COLS = '48px minmax(120px,1fr) 56px 130px 130px 80px 80px';

const RunnerLine: React.FC<{ rank: string; r: DayResultRunner; strong?: boolean }> = ({ rank, r, strong }) => (
  <div style={{ display: 'grid', gridTemplateColumns: ROW_COLS, gap: 8, padding: '4px 0', alignItems: 'center' }}>
    <span style={{ color: '#888', fontWeight: strong ? 700 : 400 }}>{rank}</span>
    <span style={{ fontWeight: strong ? 700 : 500 }}>{cleanName(r.runner_name)}</span>
    <span title="Barrier" style={{ color: '#555', fontSize: 13 }}>{r.barrier != null ? `B${r.barrier}` : '—'}</span>
    <span style={{ color: '#555', fontSize: 13 }} title="Jockey">{r.jockey || '—'}</span>
    <span style={{ color: '#555', fontSize: 13 }} title="Trainer">{r.trainer || '—'}</span>
    <span title="Win BSP">{bsp(r.win_bsp)}</span>
    <span title="Place BSP" style={{ color: '#666' }}>{bsp(r.place_bsp)}</span>
  </div>
);

// Surface chip — turf / synthetic / all-weather / dirt (the auto-vs-results join dimension).
const surfaceLabel = (s?: string | null) => (s ? s.replace('_', '-') : null);
const SurfaceChip: React.FC<{ surface?: string | null }> = ({ surface }) => {
  if (!surface) return null;
  const bg = surface === 'synthetic' ? '#7b4fd6' : surface === 'dirt' ? '#b07a3c' : surface === 'all_weather' ? '#3c9db0' : '#3aa457';
  return <span style={{ background: bg, color: '#fff', borderRadius: 4, padding: '1px 7px', fontSize: 11, fontWeight: 600, textTransform: 'capitalize' }}>{surfaceLabel(surface)}</span>;
};

const RaceBlock: React.FC<{ r: DayResultRace; showDate?: boolean }> = ({ r, showDate }) => (
  <div style={{ borderTop: '1px solid #eee', padding: '10px 0' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', color: '#333', fontWeight: 600, alignItems: 'center' }}>
      <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <span>{showDate && r.date ? `${r.date} · ` : ''}R{r.race_number}{r.race_name ? ` · ${r.race_name}` : ''}</span>
        <SurfaceChip surface={r.surface} />
      </span>
      <span style={{ color: '#999', fontWeight: 400, fontSize: 13 }}>
        {r.settled ? `Win vol $${Math.round(r.win_volume).toLocaleString()}` : 'not settled'}
      </span>
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: ROW_COLS, gap: 8, fontSize: 12, color: '#aaa', marginTop: 4 }}>
      <span></span><span></span><span>Barrier</span><span>Jockey</span><span>Trainer</span><span>Win BSP</span><span>Place BSP</span>
    </div>
    {r.winner
      ? <RunnerLine rank="1st" r={r.winner} strong />
      : <div style={{ color: '#bbb', padding: '4px 0' }}>— awaiting result —</div>}
    {r.placed.map((p, i) => <RunnerLine key={p.selection_id ?? i} rank="placed" r={p} />)}
  </div>
);

const btn = (active: boolean): React.CSSProperties => ({
  padding: '6px 14px', borderRadius: 6, border: '1px solid #ccc', cursor: 'pointer',
  background: active ? '#2d6cdf' : '#fff', color: active ? '#fff' : '#333', fontWeight: 600,
});

const DayResults: React.FC = () => {
  const today = new Date().toISOString().slice(0, 10);
  const [mode, setMode] = useState<'date' | 'all'>('date');
  const [date, setDate] = useState(today);
  const [data, setData] = useState<DayResultsData | null>(null);
  const [all, setAll] = useState<AllResults | null>(null);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const loadDay = async (d: string) => {
    setLoading(true); setErr(null);
    try { setData(await getDayResults(d)); }
    catch (e: any) { setErr(e?.message || 'Could not load results'); }
    finally { setLoading(false); }
  };
  const loadAll = async (p: number) => {
    setLoading(true); setErr(null);
    try { setAll(await getAllResults(p, 1000)); }
    catch (e: any) { setErr(e?.message || 'Could not load all results'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (mode === 'date') loadDay(date);
    else loadAll(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, date, page]);

  const goAll = () => { setMode('all'); setPage(0); };
  const goDate = () => { setMode('date'); };
  const maxWidth = mode === 'all' ? 1100 : 900;

  return (
    <div style={{ maxWidth, margin: '0 auto', padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0 }}>Race Results</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button style={btn(mode === 'date')} onClick={goDate}>By day</button>
          <button style={btn(mode === 'all')} onClick={goAll}>All</button>
          {mode === 'date' && (
            <input type="date" value={date} max={today} onChange={(e) => setDate(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ccc' }} />
          )}
        </div>
      </div>
      <p style={{ color: '#888', marginTop: 0 }}>
        Every race with Betfair Starting Price (Win · Place) plus jockey &amp; trainer. 1st is the settled
        Betfair winner; place-getters are shown by win-BSP order (Betfair doesn’t publish exact 2nd/3rd/4th).
      </p>

      {loading && <p>Loading…</p>}
      {err && <p style={{ color: '#c0392b' }}>{err}</p>}

      {/* ── BY-DAY view ── */}
      {mode === 'date' && data && !loading && (
        <>
          <p style={{ color: '#888' }}>{data.settled_races} of {data.total_races} races settled.</p>
          {data.meetings.length === 0 && <p>No races for this date.</p>}
          {data.meetings.map((m) => (
            <section key={m.meeting} style={{ marginBottom: 22, border: '1px solid #e5e5e5', borderRadius: 8, overflow: 'hidden' }}>
              <h2 style={{ margin: 0, padding: '8px 12px', background: '#f5f5f7', fontSize: 16 }}>{m.meeting}</h2>
              <div style={{ padding: '0 12px' }}>
                {m.races.map((r) => <RaceBlock key={r.market_id} r={r} />)}
              </div>
            </section>
          ))}
        </>
      )}

      {/* ── ALL view (paginated 1000/page) ── */}
      {mode === 'all' && all && !loading && (
        <>
          <Pager all={all} page={page} setPage={setPage} />
          <div style={{ border: '1px solid #e5e5e5', borderRadius: 8, overflow: 'hidden', padding: '0 12px' }}>
            {all.races.length === 0 && <p style={{ padding: 12 }}>No results.</p>}
            {all.races.map((r) => (
              <RaceBlock key={`${r.date}|${r.market_id}`} r={r} showDate />
            ))}
          </div>
          <Pager all={all} page={page} setPage={setPage} />
        </>
      )}
    </div>
  );
};

const Pager: React.FC<{ all: AllResults; page: number; setPage: (n: number) => void }> = ({ all, page, setPage }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '10px 0', color: '#666' }}>
    <span>
      {all.total_races.toLocaleString()} races · showing {all.returned.toLocaleString()} ({all.settled_races.toLocaleString()} settled) ·
      page {all.page + 1} of {all.total_pages}
    </span>
    <div style={{ display: 'flex', gap: 8 }}>
      <button style={btn(false)} disabled={page <= 0} onClick={() => setPage(Math.max(0, page - 1))}>◀ Prev</button>
      <button style={btn(false)} disabled={all.page + 1 >= all.total_pages} onClick={() => setPage(page + 1)}>Next ▶</button>
    </div>
  </div>
);

export default DayResults;
